import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { MessagesThread, DocumentsList } from "../../components/partners/PartnerDetail";

const MISSION_STATUS = { a_faire: "À faire", en_cours: "En cours", livree: "Livrée", validee: "Validée" };
const LEAD_STATUS = { nouveau: "Nouveau", qualifie: "Qualifié", gagne: "Gagné", perdu: "Perdu" };

/** Sous-traitant : missions confiées, mise à jour du statut et commentaire. */
export function PartnerMissions() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/partner/missions").then((r) => setItems(r.data)).catch((e) => toast.error(e.response?.data?.detail || e.message)), [api]);
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);
  const update = async (m, patch) => { try { await api.put(`/partner/missions/${m.mission_id}`, patch); toast.success("Mission mise à jour"); load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); } };
  if (!items) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;
  return (
    <div data-testid="partner-missions-page">
      <h1 className="text-2xl font-black text-white mb-6">Missions</h1>
      {items.length === 0 ? <div className="text-sm text-slate-500 italic">Aucune mission confiée pour le moment.</div> : items.map((m) => <MissionCard key={m.mission_id} m={m} onUpdate={update} />)}
    </div>
  );
}

function MissionCard({ m, onUpdate }) {
  const [comment, setComment] = useState(m.partner_comment || "");
  const locked = m.status === "validee";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-3" data-testid={"partner-mission-" + m.mission_id}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-white font-black flex-1">{m.title}</div>
        {m.due_date && <span className="text-xs text-slate-400">Échéance {m.due_date}</span>}
        {m.amount_ht > 0 && <span className="text-xs text-slate-300">{m.amount_ht} € HT</span>}
        <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (m.status === "validee" ? "bg-emerald-500/15 text-emerald-400" : m.status === "livree" ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/15 text-amber-400")} data-testid={"partner-mission-status-" + m.mission_id}>{MISSION_STATUS[m.status] || m.status}</span>
      </div>
      {m.description && <p className="text-xs text-slate-400 mt-2">{m.description}</p>}
      {!locked && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire / lien vers le livrable" className="bg-[#14101c] border-white/10 text-white text-xs flex-1" data-testid={"partner-mission-comment-" + m.mission_id} />
          <div className="flex sm:flex-col gap-2">
            {m.status === "a_faire" && <Button size="sm" onClick={() => onUpdate(m, { status: "en_cours", partner_comment: comment })} className="text-[#14101c] font-bold" style={{ background: "var(--brand-primary,#f7c935)" }} data-testid={"partner-mission-start-" + m.mission_id}>Démarrer</Button>}
            {m.status !== "livree" && <Button size="sm" onClick={() => onUpdate(m, { status: "livree", partner_comment: comment })} variant="outline" className="border-white/10 text-slate-200" data-testid={"partner-mission-deliver-" + m.mission_id}>Marquer livrée</Button>}
            {m.status === "livree" && <Button size="sm" onClick={() => onUpdate(m, { partner_comment: comment })} variant="outline" className="border-white/10 text-slate-200">Enregistrer le commentaire</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Apporteur d'affaires : déclaration et suivi des leads. */
export function PartnerLeads() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", need: "", estimated_amount: "" });
  const load = useCallback(() => api.get("/partner/leads").then((r) => setItems(r.data)).catch((e) => toast.error(e.response?.data?.detail || e.message)), [api]);
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.company && !form.contact_name) { toast.error("Indiquez une société ou un contact"); return; }
    try { await api.post("/partner/leads", form); setForm({ company: "", contact_name: "", email: "", phone: "", need: "", estimated_amount: "" }); toast.success("Lead déclaré"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  if (!items) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;
  return (
    <div data-testid="partner-leads-page">
      <h1 className="text-2xl font-black text-white mb-6">Leads apportés</h1>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
        <Input placeholder="Société du prospect" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="lead-company" />
        <Input placeholder="Nom du contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="lead-contact" />
        <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="lead-email" />
        <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="lead-phone" />
        <Input placeholder="Besoin / contexte" value={form.need} onChange={(e) => setForm({ ...form, need: e.target.value })} className="bg-[#14101c] border-white/10 text-white sm:col-span-2" data-testid="lead-need" />
        <Input type="number" placeholder="Montant estimé €" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="lead-amount" />
        <Button onClick={submit} className="text-[#14101c] font-bold" style={{ background: "var(--brand-primary,#f7c935)" }} data-testid="lead-submit"><Plus className="w-4 h-4 mr-1" /> Déclarer ce lead</Button>
      </div>
      {items.length === 0 ? <div className="text-sm text-slate-500 italic">Aucun lead déclaré.</div> : items.map((l) => (
        <div key={l.lead_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs mb-2 flex-wrap" data-testid={"partner-lead-" + l.lead_id}>
          <span className="text-white font-bold">{l.company || l.contact_name}</span><span className="text-slate-500 flex-1">{l.contact_name} · {l.email}</span>
          {l.estimated_amount > 0 && <span className="text-slate-300">~{l.estimated_amount} €</span>}
          <span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (l.status === "gagne" ? "bg-emerald-500/15 text-emerald-400" : l.status === "perdu" ? "bg-red-500/15 text-red-400" : "bg-sky-500/15 text-sky-300")}>{LEAD_STATUS[l.status] || l.status}</span>
        </div>
      ))}
    </div>
  );
}

export function PartnerCommissions() {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { axiosAuth().get("/partner/commissions").then((r) => setData(r.data)).catch(() => setData({ items: [], total_due: 0, total_paid: 0 })); /* eslint-disable-next-line */ }, []);
  if (!data) return <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />;
  return (
    <div data-testid="partner-commissions-page">
      <h1 className="text-2xl font-black text-white mb-6">Commissions</h1>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><div className="text-[10px] uppercase text-slate-500 font-bold">En attente</div><div className="text-2xl font-black text-amber-300" data-testid="partner-commissions-due">{data.total_due} €</div></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><div className="text-[10px] uppercase text-slate-500 font-bold">Payées</div><div className="text-2xl font-black text-emerald-300" data-testid="partner-commissions-paid">{data.total_paid} €</div></div>
      </div>
      {data.items.length === 0 ? <div className="text-sm text-slate-500 italic">Aucune commission enregistrée.</div> : data.items.map((c) => (
        <div key={c.commission_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs mb-2"><span className="text-white font-bold flex-1">{c.label}</span><span className="text-slate-300">{c.amount} €</span><span className={"text-[10px] px-1.5 py-0.5 rounded uppercase font-bold " + (c.status === "payee" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400")}>{c.status === "payee" ? "Payée" : "En attente"}</span></div>
      ))}
    </div>
  );
}

export function PartnerMessages() {
  const { axiosAuth } = useAuth();
  return (
    <div data-testid="partner-messages-page">
      <h1 className="text-2xl font-black text-white mb-6">Messagerie</h1>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><MessagesThread api={axiosAuth()} listUrl="/partner/messages" postUrl="/partner/messages" me="partner" testId="partner-messages" /></div>
    </div>
  );
}

export function PartnerDocuments() {
  const { axiosAuth } = useAuth();
  return (
    <div data-testid="partner-documents-page">
      <h1 className="text-2xl font-black text-white mb-6">Documents partagés</h1>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><DocumentsList api={axiosAuth()} listUrl="/partner/documents" uploadUrl="/partner/documents" testId="partner-documents" /></div>
    </div>
  );
}
